// src/features/system/devices/api/deviceValidationApi.ts
// ────────────────────────────────────────────────────────────────────────────
// Device Validation Run API — triggers PACDaemon validation probes
// and retrieves results from hot-stream / outcomes.
// ────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

// ── Types ────────────────────────────────────────────────────────────────

/** Single probe result from the PACDaemon validation handler. */
export interface ValidationProbe {
  probe_id:    string;           // wifi | health | sensor | publish | identity
  passed:      boolean;
  message:     string;
  measured:    unknown;          // probe-specific measured value
  reference:   unknown;          // expected / reference value
  duration_ms: number;
}

/** Baseline snapshot captured during validation. */
export interface ValidationBaseline {
  temperature_c:  number | null;
  humidity_pct:   number | null;
  rssi_dbm:       number | null;
  free_heap:      number | null;
  fw_version:     string | null;
  captured_at:    string;        // ISO timestamp
}

/** Full validation result for one device run. */
export interface ValidationResult {
  validation_status: 'PASS' | 'FAIL';
  device_id:         string;
  serial_number:     string;
  device_ip:         string;
  probes:            ValidationProbe[];
  probes_total:      number;
  probes_passed:     number;
  probes_failed:     number;
  baseline:          ValidationBaseline;
  validated_at:      string;
  validated_by:      string;
  handler_version:   string;
}

/** A digested validation outcome row (from BFF). */
export interface ValidationOutcomeRow {
  id:                number;
  run_id:            number;
  validation_status: string;
  device_id:         string;
  serial_number:     string;
  validated_at:      string;
  validated_by:      string;
  probes_total:      number;
  probes_passed:     number;
  probes_failed:     number;
  baseline:          ValidationBaseline | null;
}

/** A digested hot-stream validation event. */
export interface ValidationHotEvent {
  id:         number;
  scope_key:  string;
  category:   string;
  v_text:     string | null;
  v_jsonb:    Record<string, unknown> | null;
  created_at: string;
}

/** Payload for starting a validation run. */
export interface StartValidationRequest {
  device_id:     string;
  device_ip:     string;
  serial_number: string;
}

// ── Query keys ───────────────────────────────────────────────────────────

export const VALIDATION_KEYS = {
  history:  (deviceId: string) => ['device-validation', 'history',  deviceId] as const,
  snapshot: (deviceId: string) => ['device-validation', 'snapshot', deviceId] as const,
  all:      ['device-validation'] as const,
} as const;

// ── API base path ────────────────────────────────────────────────────────

const BASE = '/device-validation';

// ── Fetch functions ──────────────────────────────────────────────────────

/** Kick off a validation run for a device. */
export const startValidationApi = (body: StartValidationRequest) =>
  bff.post<{ run_id: number; status: string }>(`${BASE}/start`, body);

/** Fetch validation outcome history for a specific device. */
export const fetchValidationHistoryApi = async (
  deviceId: string,
): Promise<ValidationOutcomeRow[]> => {
  const res = await bff.get<{ items: ValidationOutcomeRow[] }>(
    `${BASE}/${encodeURIComponent(deviceId)}`,
  );
  return Array.isArray(res?.items) ? res.items : [];
};

/** Fetch real-time hot-stream validation events. */
export const fetchValidationSnapshotApi = async (
  deviceId: string,
): Promise<ValidationHotEvent[]> => {
  const res = await bff.get<{ items: ValidationHotEvent[] }>(
    `${BASE}/${encodeURIComponent(deviceId)}/snapshot`,
  );
  return Array.isArray(res?.items) ? res.items : [];
};

/** Submit a completed validation result from a local run. */
export const submitValidationResultApi = (result: ValidationResult) =>
  bff.post(`${BASE}/result`, result);
